import { Component, NgZone, Input, Injector } from "@angular/core";
import { ModalController, Platform, AlertController } from "@ionic/angular";
import { first } from "rxjs/operators";

import { AreaListsPage } from "src/app/pages/area-lists/area-lists.page";
import { TermsAndConditionsPage } from "src/app/pages/terms-and-conditions/terms-and-conditions.page";
import { endpoints } from "src/app/constant/endpoints";

// Services
import { ApiService } from "src/app/provider/api.service";
import { AuthService } from "src/app/services/auth.service";
import { DataService } from "src/app/services/data.service";
import { Device } from "@capacitor/device";
import { RouterStateTrackerService } from "src/app/services/router-state-tracker.service";
import { BasePage } from "src/app/shared/base-page";
import { TranslateService } from "@ngx-translate/core";

interface SignUpForm {
  areasOrRegionId: string;
  TcChecked: boolean;
  password: string;
  device_token: string;
  last_name: string;
  name: string;
  email: string;
  referral_code: string | undefined;
  phone: number;
  device_type: string;
  dialing_code: number;
}
@Component({
  selector: "signup",
  templateUrl: "./signup.component.html",
  styleUrls: ["./signup.component.scss"],
  standalone: false,
})
export class SignUpComponent extends BasePage {
  @Input() subData?: any = undefined;
  // --- form model
  signUpForm: SignUpForm = {
    areasOrRegionId: "",
    TcChecked: false,
    password: "",
    device_token: "",
    last_name: "",
    name: "",
    email: "",
    referral_code: "" as string | undefined,
    phone: null,
    device_type: "",
    dialing_code: null,
  };

  loading: boolean = false;
  // --- UI state
  showPasswordFlag = false;
  passwordInputType: "password" | "text" = "password";
  userPhoneAllowedDigit = 8;
  signUpState = "";
  areasOrRegionName = "";
  isReferralEnabled = true;

  // --- validation state
  isFirstNameValid = false;
  isLastNameValid = false;
  isEmailValid = false;
  isPhoneValid = false;
  isPasswordValid = false;
  isTcChecked = false;
  isAreasOrRegionIdValid = false;
  areaList: any = [];
  storeData?: any;
  showRef?: boolean;
  inputClasses: { [key: string]: string } = {};
  dialCode: any;

  constructor(
    injector: Injector,
    private apiService: ApiService,
    private authService: AuthService,
    private dataService: DataService,
    private modalCtrl: ModalController,
    // private translate: TranslateService,
    public override ngZone: NgZone,
    private platform: Platform,
    public override routerStateTrackerService: RouterStateTrackerService,
    private alertController: AlertController
  ) {
    super(injector);
    const araD = this.dataService.getCachedData("areas");
    this.areaList = araD?.areas || [];

    if (this.areaList.length === 0) {
      this.dataService
        .getData("areas")
        .pipe(first())
        .subscribe((res: any) => {
          this.areaList = res?.areas || [];
        });
    }

    const config = this.dataService.getCachedData("configurations");
    this.storeData = config?.organization;
    this.isReferralEnabled = this.storeData?.is_referral_enabled === 1;
    this.userPhoneAllowedDigit = this.storeData?.allowed_digit_for_phone || 10;
    this.dialCode = this.storeData?.dialing_code || '965';
    const referallenabled = this.storeData?.is_referral_enabled;
    const loyaltyenabled = this.storeData?.is_loyalty_enabled;
    if (referallenabled === 1 && loyaltyenabled === 1) {
      this.showRef = true;
    } else {
      this.showRef = false;
    }
  }

  // --- submit
  async onSubmitSignUp(): Promise<void> {
    if (!this.isValidSignupForm()) {
      this.toastMessage("please_fill_all_mandatory_fields");
      return;
    }
    this.loading = true;
    // enrich with device info
    this.signUpForm.device_type = this.platform?.is("android")
      ? "android"
      : this.platform?.is("ios")
      ? "ios"
      : "web";

    this.signUpForm.device_token = this.platform?.is("capacitor")
      ? await Device.getId().then((info) => info.identifier) // replace with await Device.getId().then(info => info.identifier) if using Capacitor Device
      : "demo";

    if (!this.isReferralEnabled) {
      this.signUpForm.referral_code = undefined;
    }
    const storageLoginData = {
      phone: Number(this.signUpForm.phone),
      password: this.signUpForm.password,
    };
    try {
      const response = await this.apiService
        .post(endpoints.register, this.signUpForm)
        .pipe(first())
        .toPromise();

      if (response?.success) {
        const customerData = response.data?.customer;
        // Save auth data (customer.is_verified will be false for new signups)
        await this.authService.setAuthToken(customerData.session_token);
        await this.authService.setAuthUser(customerData);
        await this.configService.setStorage('loginData', storageLoginData);
        
        this.loading = false;

        // Navigate to OTP (signup always requires verification)
        // Determine redirect route based on context:
        // 1. If subData exists → subscription checkout flow
        // 2. If checkoutSlug is "order" → order flow → customer-details
        // 3. Otherwise → normal signup → subscription-list
        let redirectRoute = "subscription-list";
        const checkoutSlug = await this.configService.getStorage("checkoutSlug");

        if (this.subData) {
          redirectRoute = "checkout";
        } else if (checkoutSlug === "order") {
          redirectRoute = "customer-details";
        }

        const navigationPayload: Record<string, unknown> = {
          phone: this.signUpForm.phone,
          email: this.signUpForm.email,
          dialing_code: this.dialCode,
          otp_sent: true,
          context: "signup",
          redirectTo: redirectRoute,
          historyRoute: "onboarding",
        };

        if (this.subData) {
          navigationPayload["checkoutPayload"] = this.subData;
        }

        this.navigateToPage("otp-verification", navigationPayload);
      } else {
        this.loading = false;
        this.toastMessage(response?.message || "sign_up_failed");
      }
    } catch (err: any) {
      console.error("Sign up error:", err);
      this.loading = false;

      // Mark fields with errors for visual feedback
      this.markAllFieldsAsError();

      if (err.status === 422) {
        this.toastMessage("please_fill_all_mandatory_fields");
      } else if (err.status === 400 && err.error?.message) {
        this.toastMessage(err.error.message);
      } else {
        this.toastMessage("an_unexpected_error_occurred");
      }
    }
  }

  // --- validation
  isValidSignupForm(): boolean {
    this.isFirstNameValid = (this.signUpForm.name?.trim()?.length || 0) > 2;
    this.isLastNameValid = (this.signUpForm.last_name?.trim()?.length || 0) > 2;
    this.isEmailValid = (this.signUpForm.email?.trim()?.length || 0) > 5;
    this.isPhoneValid = (this.signUpForm.phone?.toString().trim()?.length || 0) > 5;
    this.isPasswordValid = (this.signUpForm.password?.trim()?.length || 0) > 5;
    this.isTcChecked = this.signUpForm.TcChecked === true;

    this.isAreasOrRegionIdValid =
      (Array.isArray(this.signUpForm.areasOrRegionId) &&
        this.signUpForm.areasOrRegionId.length > 0) ||
      (typeof this.signUpForm.areasOrRegionId === "string" &&
        this.signUpForm.areasOrRegionId.length > 0) ||
      (typeof this.signUpForm.areasOrRegionId === "number" &&
        this.signUpForm.areasOrRegionId > 0);

    // Update input classes for visual feedback
    this.updateInputClasses();

    return (
      this.isFirstNameValid &&
      this.isLastNameValid &&
      this.isEmailValid &&
      this.isPhoneValid &&
      this.isPasswordValid &&
      this.isTcChecked &&
      this.isAreasOrRegionIdValid
    );
  }

  // --- Update input classes based on validation
  private updateInputClasses(): void {
    this.inputClasses = {
      first_name: this.isFirstNameValid ? "" : "field-error",
      last_name: this.isLastNameValid ? "" : "field-error",
      email: this.isEmailValid ? "" : "field-error",
      phone: this.isPhoneValid ? "" : "field-error",
      password: this.isPasswordValid ? "" : "field-error",
      area: this.isAreasOrRegionIdValid ? "" : "field-error",
    };
  }

  // --- Mark all fields as error (for API errors)
  markAllFieldsAsError(): void {
    this.inputClasses = {
      first_name: "field-error",
      last_name: "field-error",
      email: "field-error",
      phone: "field-error",
      password: "field-error",
      area: "field-error",
    };
  }

  // --- Clear all error states
  clearErrorStates(): void {
    this.inputClasses = {};
  }

  // --- input handling
  onPhoneInput(event: Event): void {
    const input = (event.target as HTMLInputElement).value.replace(/\D/g, "");
    this.signUpForm.phone = Number(input.slice(0, this.userPhoneAllowedDigit));
  }

  togglePasswordVisibility(): void {
    this.showPasswordFlag = !this.showPasswordFlag;
    this.passwordInputType = this.showPasswordFlag ? "text" : "password";
  }

  // --- modals
  async openAreaModal(): Promise<void> {
    const areaModal = await this.modalCtrl?.create({
      component: AreaListsPage,
      componentProps: { citylist: this.areaList }, // pass actual list
    });
    await areaModal.present();
    const { data } = await areaModal.onDidDismiss();
    if (data) {
      this.signUpForm.areasOrRegionId = data.id;
      this.areasOrRegionName = data.title;
    }
  }

  async openTermsAndConditionsModal(): Promise<void> {
    const termsModal = await this.modalCtrl?.create({
      component: TermsAndConditionsPage,
      cssClass: "basic-modal",
    });
    await termsModal.present();
    const { data } = await termsModal.onDidDismiss();
    this.signUpForm.TcChecked = data || false;
  }

  // --- utils
  // private navigateToPage(path: string, extras?: any): void {
  //   this.router?.navigate([path], { queryParams: extras || {} });
  // }
  override navigateToPage(
    page: string,
    data: any = { test: "From Onb" }
  ): void {
    // const main = this.routerStateTrackerService.combinedData;
    // this.routerStateTrackerService.navigateWithPreExitAnimation(page, data);
    super.navigateToPage(page, data);
    // this.router?.navigate([page], navigationExtras);
  }
  private async toastMessage(message: string): Promise<void> {
    const alert = await this.alertController.create({
      header: "Alert",
      message: this.translate?.instant(message),
      buttons: ["OK"],
    });
    await alert.present();
  }
}
